import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';

// ── Physics ────────────────────────────────────────────────────────────────────
const W = 400;
const H = 600;
const GROUND = 112;
const BIRD_X = Math.floor(W / 3);  // 133
const BIRD_R = 15;
const GAP = 200;          // wider gap → more forgiving
const PIPE_W = 52;
const PIPE_SPEED = 2.0;   // slower pipes
const GRAVITY = 0.26;     // gentler fall
const FLAP_V = -6.0;      // softer flap
const SPAWN_PX = 420;     // more space between pipes

// Fixed-timestep: physics always advance at 60 fps regardless of monitor Hz.
// On a 120 Hz screen, rAF fires every 8 ms; without this fix the game runs
// exactly 2× faster than intended.
const STEP_MS = 1000 / 60; // 16.667 ms

// ── MediaPipe landmark indices ─────────────────────────────────────────────────
const LM = { L_SHOULDER: 11, R_SHOULDER: 12, L_ELBOW: 13, R_ELBOW: 14, L_WRIST: 15, R_WRIST: 16 };

// Connections to draw on camera overlay (matching MediaPipe pose skeleton)
const SKELETON_EDGES = [
  [LM.L_SHOULDER, LM.R_SHOULDER],
  [LM.L_SHOULDER, LM.L_ELBOW],
  [LM.L_ELBOW,    LM.L_WRIST],
  [LM.R_SHOULDER, LM.R_ELBOW],
  [LM.R_ELBOW,    LM.R_WRIST],
];

function getArmStates(landmarks) {
  if (!landmarks || landmarks.length < 17) return { left: false, right: false };
  const ls = landmarks[LM.L_SHOULDER], rs = landmarks[LM.R_SHOULDER];
  const lw = landmarks[LM.L_WRIST],   rw = landmarks[LM.R_WRIST];
  if (!ls || !rs || !lw || !rw) return { left: false, right: false };
  return { left: lw.y < ls.y, right: rw.y < rs.y };
}

// ── Draw pose skeleton on the overlay canvas ──────────────────────────────────
function drawSkeleton(ctx, landmarks, cw, ch, armState) {
  if (!landmarks || landmarks.length < 17) return;
  ctx.clearRect(0, 0, cw, ch);

  // Connection lines
  ctx.lineWidth = 2;
  for (const [a, b] of SKELETON_EDGES) {
    const pa = landmarks[a], pb = landmarks[b];
    if (!pa || !pb) continue;
    // Mirror X because the video is flipped
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.beginPath();
    ctx.moveTo((1 - pa.x) * cw, pa.y * ch);
    ctx.lineTo((1 - pb.x) * cw, pb.y * ch);
    ctx.stroke();
  }

  // Key landmark dots
  const keyLMs = [LM.L_SHOULDER, LM.R_SHOULDER, LM.L_ELBOW, LM.R_ELBOW, LM.L_WRIST, LM.R_WRIST];
  for (const idx of keyLMs) {
    const p = landmarks[idx];
    if (!p) continue;
    const isLeft  = idx === LM.L_WRIST || idx === LM.L_ELBOW || idx === LM.L_SHOULDER;
    const isRight = idx === LM.R_WRIST || idx === LM.R_ELBOW || idx === LM.R_SHOULDER;
    const up = (isLeft && armState.left) || (isRight && armState.right);
    ctx.fillStyle = up ? '#00ff88' : '#00ccff';
    ctx.beginPath();
    ctx.arc((1 - p.x) * cw, p.y * ch, idx === LM.L_WRIST || idx === LM.R_WRIST ? 7 : 5, 0, Math.PI * 2);
    ctx.fill();
  }

  // "Raise your arms!" text at the bottom of the overlay (like the original)
  const raised = armState.left || armState.right;
  let statusTxt = 'Raise your arms!';
  if (armState.left && armState.right) statusTxt = 'BOTH ARMS UP!';
  else if (armState.left)  statusTxt = 'LEFT ARM UP!';
  else if (armState.right) statusTxt = 'RIGHT ARM UP!';

  ctx.font = 'bold 13px Tahoma, Arial, sans-serif';
  const tw = ctx.measureText(statusTxt).width;
  const tx = cw / 2 - tw / 2;
  const ty = ch - 10;
  ctx.fillStyle = raised ? '#00ff88' : 'rgba(255,255,255,0.8)';
  ctx.fillText(statusTxt, tx, ty);

  // L / R boxes in corners (matches original game)
  const boxW = 28, boxH = 22, pad = 6;
  // LEFT box — left corner
  ctx.fillStyle = armState.left ? '#00c853' : 'rgba(0,0,0,0.55)';
  ctx.fillRect(pad, pad, boxW, boxH);
  ctx.strokeStyle = armState.left ? '#69f0ae' : '#546e7a';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(pad, pad, boxW, boxH);
  ctx.fillStyle = armState.left ? '#fff' : '#90a4ae';
  ctx.font = 'bold 12px Tahoma, Arial, sans-serif';
  ctx.fillText('L', pad + boxW / 2 - 4, pad + boxH - 6);

  // RIGHT box — right corner
  ctx.fillStyle = armState.right ? '#00c853' : 'rgba(0,0,0,0.55)';
  ctx.fillRect(cw - pad - boxW, pad, boxW, boxH);
  ctx.strokeStyle = armState.right ? '#69f0ae' : '#546e7a';
  ctx.strokeRect(cw - pad - boxW, pad, boxW, boxH);
  ctx.fillStyle = armState.right ? '#fff' : '#90a4ae';
  ctx.fillText('R', cw - pad - boxW / 2 - 4, pad + boxH - 6);
}

// ── Lazy-load MediaPipe so WASM failures only break camera, not the game ───────
let mpPromise = null;
function loadMediaPipe() {
  if (mpPromise) return mpPromise;
  mpPromise = import('@mediapipe/tasks-vision').catch(e => { mpPromise = null; throw e; });
  return mpPromise;
}

async function createPoseLandmarker() {
  const { FilesetResolver, PoseLandmarker } = await loadMediaPipe();
  const version = '0.10.34';
  const wasmBase = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${version}/wasm`;
  const modelUrl =
    'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
  const fileset = await FilesetResolver.forVisionTasks(wasmBase);
  const cfg = { runningMode: 'VIDEO', numPoses: 1,
    minPoseDetectionConfidence: 0.5, minPosePresenceConfidence: 0.5, minTrackingConfidence: 0.5 };
  try {
    return await PoseLandmarker.createFromOptions(fileset, { baseOptions: { modelAssetPath: modelUrl, delegate: 'GPU' }, ...cfg });
  } catch {
    return await PoseLandmarker.createFromOptions(fileset, { baseOptions: { modelAssetPath: modelUrl, delegate: 'CPU' }, ...cfg });
  }
}

async function getCameraStream() {
  if (!window.isSecureContext) throw new Error('Camera requires localhost or HTTPS.');
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('Camera not supported. Try Chrome or Firefox.');
  return navigator.mediaDevices.getUserMedia({
    video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, audio: false,
  });
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function BigFlappyBird() {
  const canvasRef      = useRef(null);
  const overlayRef     = useRef(null); // camera skeleton overlay canvas
  const rafRef         = useRef(null);
  const poseRafRef     = useRef(null);
  const videoRef       = useRef(null);
  const poseLandmarkerRef = useRef(null);
  const streamRef      = useRef(null);
  const armsWereUpRef  = useRef(false);
  const armStateRef    = useRef({ left: false, right: false });
  const flapRef        = useRef(() => {});
  // Fixed-timestep accumulators
  const accRef         = useRef(0);
  const lastTsRef      = useRef(null);

  const gs = useRef({ mode: 'menu', birdY: H / 2, vy: 0, pipes: [], nextPipeX: W + 100, score: 0, tick: 0 });

  const [score, setScore]       = useState(0);
  const [gameMode, setGameMode] = useState('menu');
  const [camState, setCamState] = useState('off');
  const [camError, setCamError] = useState('');
  // armState is only used for the DOM arm-indicator boxes — updated at ~10 fps to avoid lag
  const [armState, setArmState] = useState({ left: false, right: false });

  // ── Game canvas drawing ────────────────────────────────────────────────────
  const drawFrame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const s = gs.current;

    ctx.fillStyle = '#87ceeb';
    ctx.fillRect(0, 0, W, H - GROUND);

    // Clouds
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    [[60, 48, 38], [200, 68, 32], [320, 42, 35]].forEach(([cx, cy, cr]) => {
      ctx.beginPath();
      ctx.arc(cx, cy, cr, 0, Math.PI * 2);
      ctx.arc(cx + cr * 0.8, cy - 5, cr * 0.7, 0, Math.PI * 2);
      ctx.arc(cx - cr * 0.65, cy - 3, cr * 0.6, 0, Math.PI * 2);
      ctx.fill();
    });

    // Pipes
    for (const p of s.pipes) {
      const topH = p.gapY - GAP / 2, botY = p.gapY + GAP / 2;
      ctx.fillStyle = '#00b400';
      ctx.fillRect(p.x, 0, PIPE_W, topH);
      ctx.fillRect(p.x, botY, PIPE_W, H - GROUND - botY);
      ctx.fillStyle = '#009000';
      ctx.fillRect(p.x - 4, topH - 20, PIPE_W + 8, 20);
      ctx.fillRect(p.x - 4, botY, PIPE_W + 8, 20);
    }

    // Ground
    ctx.fillStyle = '#b48c50';
    ctx.fillRect(0, H - GROUND, W, GROUND);
    ctx.fillStyle = '#c8a060';
    ctx.fillRect(0, H - GROUND, W, 18);

    // Bird
    ctx.save();
    ctx.translate(BIRD_X, s.birdY);
    ctx.rotate(Math.max(-0.5, Math.min(1.0, s.vy * 0.08)));
    ctx.fillStyle = '#ffdc00'; ctx.strokeStyle = '#e6b800'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.ellipse(0, 0, BIRD_R + 2, BIRD_R, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#ff8c00';
    ctx.beginPath(); ctx.moveTo(BIRD_R - 2, 2); ctx.lineTo(BIRD_R + 12, 5); ctx.lineTo(BIRD_R - 2, 9); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(4, -5, 5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#222'; ctx.beginPath(); ctx.arc(6, -5, 2, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    if (s.mode === 'play') {
      const txt = String(s.score);
      ctx.font = 'bold 34px Tahoma, Arial, sans-serif';
      const tx = W / 2 - ctx.measureText(txt).width / 2;
      ctx.lineWidth = 5; ctx.strokeStyle = 'rgba(0,0,0,0.55)'; ctx.strokeText(txt, tx, 58);
      ctx.fillStyle = '#fff'; ctx.fillText(txt, tx, 58);
    }
  }, []);

  // ── Collision ──────────────────────────────────────────────────────────────
  const hitTest = useCallback((birdY, pipes) => {
    const top = birdY - BIRD_R, bot = birdY + BIRD_R;
    const lft = BIRD_X - BIRD_R, rgt = BIRD_X + BIRD_R;
    if (bot >= H - GROUND || top <= 0) return true;
    for (const p of pipes) {
      if (rgt < p.x || lft > p.x + PIPE_W) continue;
      if (top < p.gapY - GAP / 2 || bot > p.gapY + GAP / 2) return true;
    }
    return false;
  }, []);

  // ── Game loop (fixed-timestep at 60 fps) ──────────────────────────────────
  const loop = useCallback((timestamp) => {
    const s = gs.current;
    if (s.mode !== 'play') { drawFrame(); return; }

    // Initialise on first frame
    if (lastTsRef.current === null) lastTsRef.current = timestamp;
    // Clamp delta so a long pause (tab switch) doesn't cause a physics explosion
    const delta = Math.min(timestamp - lastTsRef.current, 100);
    lastTsRef.current = timestamp;
    accRef.current += delta;

    // Advance physics in fixed 16.667 ms increments → always 60 fps equivalent
    let died = false;
    while (accRef.current >= STEP_MS) {
      accRef.current -= STEP_MS;

      s.vy += GRAVITY; s.birdY += s.vy; s.tick += 1;

      for (const p of s.pipes) {
        p.x -= PIPE_SPEED;
        if (!p.passed && p.x + PIPE_W < BIRD_X) { p.passed = true; s.score += 1; setScore(s.score); }
      }
      s.pipes = s.pipes.filter(p => p.x > -PIPE_W - 20);

      s.nextPipeX -= PIPE_SPEED;
      if (s.nextPipeX <= 0) {
        const minY = GAP / 2 + 60, maxY = H - GROUND - GAP / 2 - 60;
        s.pipes.push({ x: W + 20, gapY: minY + Math.random() * (maxY - minY), passed: false });
        s.nextPipeX = SPAWN_PX;
      }

      if (hitTest(s.birdY, s.pipes)) { died = true; break; }
    }

    if (died) {
      s.mode = 'over';
      cancelAnimationFrame(rafRef.current); rafRef.current = null;
      drawFrame(); setGameMode('over'); return;
    }

    drawFrame();
    rafRef.current = requestAnimationFrame(loop);
  }, [drawFrame, hitTest]);

  // ── Flap ──────────────────────────────────────────────────────────────────
  const flap = useCallback(() => {
    const s = gs.current;
    if (s.mode === 'over') return;
    if (s.mode === 'menu') {
      s.mode = 'play'; s.birdY = H / 2; s.vy = FLAP_V;
      s.pipes = []; s.nextPipeX = W + 100; s.score = 0; s.tick = 0;
      armsWereUpRef.current = true;
      accRef.current = 0; lastTsRef.current = null; // reset fixed-timestep clock
      setScore(0); setGameMode('play');
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(loop);
    } else if (s.mode === 'play') {
      s.vy = FLAP_V;
    }
  }, [loop]);

  const restart = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    gs.current = { mode: 'menu', birdY: H / 2, vy: 0, pipes: [], nextPipeX: W + 100, score: 0, tick: 0 };
    armsWereUpRef.current = false;
    accRef.current = 0; lastTsRef.current = null;
    setScore(0); setGameMode('menu'); drawFrame();
  }, [drawFrame]);

  flapRef.current = flap;

  useEffect(() => { drawFrame(); }, [drawFrame]);

  // ── Keyboard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = e => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      e.preventDefault();
      if (gs.current.mode === 'over') restart(); else flap();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flap, restart]);

  // ── Camera ────────────────────────────────────────────────────────────────
  async function enableCamera() {
    setCamError(''); setCamState('loading');
    try {
      const stream = await getCameraStream();
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error('Video element missing.');
      video.srcObject = stream; video.playsInline = true; video.muted = true;
      await video.play();
      const lm = await createPoseLandmarker();
      poseLandmarkerRef.current = lm;
      setCamState('ready');
    } catch (err) {
      streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null;
      setCamState('error'); setCamError(err.message || 'Could not open camera or load pose model.');
    }
  }

  function stopCamera() {
    if (poseRafRef.current) { cancelAnimationFrame(poseRafRef.current); poseRafRef.current = null; }
    streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null;
    poseLandmarkerRef.current?.close?.(); poseLandmarkerRef.current = null;
    armsWereUpRef.current = false;
    armStateRef.current = { left: false, right: false };
    // Clear overlay canvas
    const ov = overlayRef.current;
    if (ov) ov.getContext('2d').clearRect(0, 0, ov.width, ov.height);
    setArmState({ left: false, right: false });
    setCamState('off'); setCamError('');
  }

  // ── Pose loop ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (camState !== 'ready' || !poseLandmarkerRef.current || !videoRef.current) {
      if (poseRafRef.current) { cancelAnimationFrame(poseRafRef.current); poseRafRef.current = null; }
      return;
    }
    const video = videoRef.current;
    let domUpdateTick = 0; // throttle React state updates to ~10 fps

    const poseLoop = () => {
      if (!poseLandmarkerRef.current) return;

      if (video.readyState >= 2 && video.videoWidth > 0) {
        try {
          const result = poseLandmarkerRef.current.detectForVideo(video, performance.now());
          const ov = overlayRef.current;

          if (result.landmarks?.length > 0) {
            const landmarks = result.landmarks[0];
            const arms = getArmStates(landmarks);
            const raised = arms.left || arms.right;

            // Draw skeleton overlay on the camera canvas
            if (ov) {
              drawSkeleton(ov.getContext('2d'), landmarks, ov.width, ov.height, arms);
            }

            // Edge-triggered flap
            const s = gs.current;
            if (raised && !armsWereUpRef.current && (s.mode === 'play' || s.mode === 'menu')) {
              flapRef.current();
            }
            armsWereUpRef.current = raised;
            armStateRef.current = arms;

            // Only update React DOM state at ~10 fps to avoid re-render lag
            domUpdateTick += 1;
            if (domUpdateTick % 6 === 0) {
              setArmState({ ...arms });
            }
          } else {
            if (ov) ov.getContext('2d').clearRect(0, 0, ov.width, ov.height);
            armsWereUpRef.current = false;
            armStateRef.current = { left: false, right: false };
            domUpdateTick += 1;
            if (domUpdateTick % 6 === 0) setArmState({ left: false, right: false });
          }
        } catch { /* dropped frame */ }
      }
      poseRafRef.current = requestAnimationFrame(poseLoop);
    };

    poseRafRef.current = requestAnimationFrame(poseLoop);
    return () => { if (poseRafRef.current) { cancelAnimationFrame(poseRafRef.current); poseRafRef.current = null; } };
  }, [camState]);

  // ── Unmount cleanup ───────────────────────────────────────────────────────
  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (poseRafRef.current) cancelAnimationFrame(poseRafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    poseLandmarkerRef.current?.close?.();
  }, []);

  return (
    <Root>
      {/* ── Game canvas (left) ── */}
      <GameSection>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          onClick={() => { if (gameMode !== 'over') flap(); }}
          onKeyDown={e => e.stopPropagation()}
          tabIndex={0}
          style={{ height: '100%', width: 'auto', display: 'block', cursor: 'pointer' }}
        />

        {gameMode === 'menu' && (
          <Overlay>
            <div className="ov-title">BIG FLAPPY BIRD</div>
            <button className="ov-btn primary" onClick={flap}>▶  Start</button>
            {camState === 'off'     && <button className="ov-btn camera" onClick={enableCamera}>📷  Enable Camera</button>}
            {camState === 'loading' && <div className="ov-info">⏳ Loading pose model…</div>}
            {camState === 'error'   && <div className="ov-err">{camError}</div>}
            {camState === 'ready'   && <button className="ov-btn off" onClick={stopCamera}>✕  Turn off camera</button>}
            <div className="ov-hint">Space / click to flap · raise arms with camera</div>
          </Overlay>
        )}

        {gameMode === 'over' && (
          <Overlay>
            <div className="ov-title">GAME OVER</div>
            <div className="ov-score">Score: {score}</div>
            <button className="ov-btn primary" onClick={restart}>↺  Play Again</button>
            <div className="ov-hint">or press Space</div>
          </Overlay>
        )}
      </GameSection>

      {/* ── Camera panel (right) ── */}
      <CamPanel>
        <div className="panel-title">Camera Control</div>

        {/* Video + skeleton overlay stacked */}
        <div className="feed-wrap">
          <video ref={videoRef} playsInline muted className={camState === 'ready' ? 'visible' : ''} />
          {/* Overlay canvas for skeleton — same dimensions as video */}
          <canvas
            ref={overlayRef}
            width={640}
            height={480}
            className="skeleton-canvas"
          />
          {camState !== 'ready' && (
            <div className="feed-off"><span>Camera Off</span></div>
          )}
        </div>

        {/* Arm boxes below the feed */}
        <div className="arm-row">
          <ArmBox active={armState.left}>
            <span className="arm-label">L ARM</span>
            <span className="arm-icon">{armState.left ? '▲ UP' : '— —'}</span>
          </ArmBox>
          <ArmBox active={armState.right}>
            <span className="arm-label">R ARM</span>
            <span className="arm-icon">{armState.right ? '▲ UP' : '— —'}</span>
          </ArmBox>
        </div>

        <div className="instructions">
          <p>Raise <strong>either wrist above your shoulder</strong> to flap.</p>
          <p>Works in good lighting facing the camera.</p>
          <p>Space or click as fallback.</p>
        </div>

        {camState === 'off'     && <button className="panel-btn" onClick={enableCamera}>📷  Enable Camera</button>}
        {camState === 'loading' && <div className="panel-info">Loading MediaPipe Pose Landmarker…</div>}
        {camState === 'error'   && (
          <><div className="panel-err">{camError}</div><button className="panel-btn" onClick={enableCamera}>Retry</button></>
        )}
        {camState === 'ready'   && <button className="panel-btn off" onClick={stopCamera}>Turn Off Camera</button>}
      </CamPanel>
    </Root>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  background: #0a1128;
  font-family: Tahoma, Arial, sans-serif;
  overflow: hidden;
`;

const GameSection = styled.div`
  position: relative;
  flex: 0 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0d1642;
  border-right: 2px solid #1a237e;
  overflow: hidden;

  canvas {
    image-rendering: pixelated;
    image-rendering: crisp-edges;
    box-shadow: 0 0 20px rgba(0,0,0,0.6);
    max-height: 100%;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #fff;
  text-align: center;
  padding: 20px;
  z-index: 10;

  .ov-title { font-size: 22px; font-weight: bold; color: #ffdc00; text-shadow: 2px 2px 0 #000; letter-spacing: 2px; }
  .ov-score { font-size: 18px; font-weight: bold; color: #87ceeb; }
  .ov-hint  { font-size: 10px; color: rgba(255,255,255,0.5); margin-top: 4px; }
  .ov-info  { font-size: 11px; color: #90caf9; }
  .ov-err   { font-size: 10px; color: #ff8a65; max-width: 240px; }

  .ov-btn {
    padding: 9px 26px; font-size: 13px; font-weight: bold;
    border: 2px solid; cursor: pointer; border-radius: 2px;
    &:active { transform: translateY(1px); }
  }
  .ov-btn.primary { background: #2e7d32; color: #fff; border-color: #66bb6a; font-size: 14px; &:hover { background: #388e3c; } }
  .ov-btn.camera  { background: #1565c0; color: #fff; border-color: #42a5f5; &:hover { background: #1976d2; } }
  .ov-btn.off     { background: #37474f; color: #cfd8dc; border-color: #546e7a; font-size: 11px; padding: 5px 14px; &:hover { background: #455a64; } }
`;

const CamPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 10px;
  background: #0a1128;
  overflow-y: auto;

  .panel-title {
    font-size: 11px; font-weight: bold; color: #5c6bc0;
    letter-spacing: 1px; text-transform: uppercase;
  }

  .feed-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    background: #000;
    border: 2px solid #1a237e;
    border-radius: 3px;
    overflow: hidden;
    flex-shrink: 0;
  }

  video {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: cover;
    transform: scaleX(-1);
    display: none;
    &.visible { display: block; }
  }

  /* Skeleton overlay canvas — sits on top of the video */
  .skeleton-canvas {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    z-index: 2;
  }

  .feed-off {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    color: #263238; font-size: 11px;
  }

  .arm-row { display: flex; gap: 6px; }

  .instructions {
    font-size: 10px; color: #37474f; line-height: 1.6;
    p { margin: 0; }
    strong { color: #546e7a; }
  }

  .panel-btn {
    width: 100%; padding: 8px; background: #1565c0; color: #fff;
    border: 2px solid #42a5f5; font-size: 12px; font-weight: bold;
    cursor: pointer; border-radius: 2px;
    &:hover { background: #1976d2; }
    &.off { background: #37474f; border-color: #546e7a; &:hover { background: #455a64; } }
  }
  .panel-info { font-size: 11px; color: #90caf9; text-align: center; }
  .panel-err  { font-size: 10px; color: #ff8a65; text-align: center; }
`;

const ArmBox = styled.div`
  flex: 1;
  display: flex; flex-direction: column; align-items: center;
  padding: 8px 4px; border-radius: 3px;
  border: 2px solid ${p => p.active ? '#4caf50' : '#1a237e'};
  background: ${p => p.active ? '#1b5e20' : '#0d1642'};
  transition: background 0.08s, border-color 0.08s;

  .arm-label { font-size: 9px; font-weight: bold; letter-spacing: 1px; color: ${p => p.active ? '#a5d6a7' : '#37474f'}; }
  .arm-icon  { font-size: 12px; font-weight: bold; color: ${p => p.active ? '#69f0ae' : '#263238'}; margin-top: 3px; }
`;
